import { FC, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { WalletMultiButton } from '@solana/wallet-adapter-material-ui';
import {
  ArrowBack,
  DoubleArrow,
  IndeterminateCheckBoxRounded,
  AddBoxRounded,
} from '@material-ui/icons';
import {
  getDisplayAmount,
  getBuyerATABalance,
  getWalletLamports,
} from '../../lib/accounts';
import {
  Button,
  IconButton,
  Typography,
  TextField,
  CircularProgress,
} from '@material-ui/core';
import { useHistory } from 'react-router';
import { sleep } from '@project-serum/common';

import { useProgramApis } from '../../hooks/useProgramApis';
import Countdown from '../../components/Countdown';
import PrizeShowcaseOngoing from './components/PrizeShowcaseOngoing';
import RaffleInfoSection from '../../components/RaffleInfoSection';
import { PurchaseTickets } from './components/PurchaseTicketsSection/PurchaseTicket';
import { routes } from '../../router/routes';
import Screen from '../../components/layout/Screen';
import useCommonStyles from '../../assets/styles';
import { useStyles } from './styles';
import PrizeGalleryOngoing from './components/PrizeGalleryOngoing';
import { useViewport } from '../../hooks/useViewport';
import { DeviceType } from '../../providers/ViewportProvider';
import Spacer from '../../components/Spacer';
import { PaymentOption, Raffle } from '../../lib/types';
import { Mixpanel } from '../../lib/Mixpanel';
import toast from 'react-hot-toast';

import {
  buyTickets,
  BUY_TICKETS_TX_FEE_LAMPORTS,
  calculateBasketPrice,
} from '../../lib/actions/buyTickets';
//custom
import { PublicKey } from '@solana/web3.js';
import { tokenInfoMap, wrappedSOL } from '../../config/tokenRegistry';
import Participants from './components/Participants';
import { u64 } from '@solana/spl-token';
import { MAX_NUMBER_OF_PARTICIPANTS } from '../../config/misc';

import Footer from "../../components/layout/Footer"
interface IRaffleOngoingScreenProps {
  raffle: Raffle;
  updateRaffle: () => void;
}

const isLamportsEnough = (lamports: number | undefined) =>
  (lamports ?? 0) >= BUY_TICKETS_TX_FEE_LAMPORTS;

interface AccountBalance {
  mint: PublicKey;
  amount: u64 | undefined;
}

interface PurchaseTicketsProps {
  raffle: Raffle;
  updateRaffle: () => void;
}
const RaffleOngoingScreen: FC<IRaffleOngoingScreenProps> = ({
  raffle,
  updateRaffle,
}) => {
  const nativePaymentOption = useMemo(
    () => ({
      mint: raffle.proceeds.mint,
      dispenserPriceIn: new u64(1),
      dispenserPriceOut: new u64(1),
    }),
    [raffle]
  );
  const { draffleClient, dispenserClient } = useProgramApis();
  const [purchaseOngoing, setPurchaseOngoing] = useState(false);
  const [walletLamports, setWalletLamports] = useState<number>();
  const { device } = useViewport();
  const classes = { ...useCommonStyles(), ...(useStyles({ device }) as any) };
  const [showParticipants, setShowParticipants] = useState(false);
  const { push } = useHistory();
  const [ticketAmount, setTicketAmount] = useState<number>(1);
  const [paymentOption, setPaymentOption] =
    useState<PaymentOption>(nativePaymentOption);
  const prizeGalleryRef = useRef<HTMLDivElement>(null);

  const [buyerATABalance, setBuyerATABalance] = useState<AccountBalance>({
    mint: raffle.proceeds.mint.publicKey,
    amount: undefined,
  });
  const entrant = useMemo(() => {
    if (!draffleClient.provider.wallet?.publicKey) return;
    return raffle?.entrants.get(
      draffleClient.provider.wallet.publicKey.toString()
    );
  }, [raffle, draffleClient.provider.wallet?.publicKey]); // "Unnecessary" dependency required due to React not picking up change in publicKey subfield

  const getBasketPrice = useCallback(
    (ticketAmount: number) =>
      calculateBasketPrice(raffle.proceeds.ticketPrice, ticketAmount),
    [raffle.proceeds.ticketPrice, paymentOption]
  );
  const lamportsEnough = useMemo(
    () => isLamportsEnough(walletLamports),
    [walletLamports]
  );

  const buyerTokenBalance = useMemo(() => {
    return paymentOption.mint.address === wrappedSOL
      ? {
          mint: new PublicKey(wrappedSOL),
          amount: new u64(walletLamports ?? 0),
        } // We ignore the potential wSOL ATA
      : buyerATABalance;
  }, [walletLamports, buyerATABalance, paymentOption.mint.address]);

  const hasEnoughFunds = useMemo(() => {
    const tokensEnough = buyerTokenBalance.amount?.gte(
      getBasketPrice(ticketAmount)
    );
    return tokensEnough && lamportsEnough;
  }, [buyerTokenBalance, lamportsEnough, ticketAmount, getBasketPrice]);

  const hasEnoughFundsToIncrementTicket = useMemo(() => {
    const tokensEnough = buyerTokenBalance.amount?.gte(
      getBasketPrice(ticketAmount + 1)
    );
    return tokensEnough && lamportsEnough;
  }, [buyerTokenBalance, lamportsEnough, ticketAmount, getBasketPrice]);

  const onBuyTickets = useCallback(async () => {
    try {
      setPurchaseOngoing(true);
      const buyerATAExists = buyerATABalance.amount !== undefined;
      // console.log(buyerATAExists, 'buyerATAExists', buyerATABalance?.amount)
      await buyTickets(
        draffleClient,
        dispenserClient,
        raffle,
        ticketAmount,
        paymentOption,
        buyerATAExists
      );
      setTicketAmount(1);
      await sleep(500);
      updateRaffle();
      toast.success(`You bought ${ticketAmount} ticket(s)`);
    } catch (error: any) {
      if (error.msg) {
        toast.error(`Transaction failed: ${error.msg}`);
      } else {
        toast.error('Unexpected error');
      }
    }
    setPurchaseOngoing(false);
  }, [
    draffleClient,
    dispenserClient,
    raffle,
    ticketAmount,
    paymentOption,
    buyerATABalance,
    setTicketAmount,
    updateRaffle,
  ]);
   useEffect(() => {
     if (!draffleClient.provider.wallet.publicKey) return;
     async function updateBuyerATABalance() {
       setBuyerATABalance({
         mint: paymentOption.mint.publicKey,
         amount: await getBuyerATABalance(
           draffleClient.provider,
           paymentOption.mint.publicKey
         ),
       });
     }
     const timerId = setInterval(() => {
       updateBuyerATABalance();
     }, 5000);
     updateBuyerATABalance();
     return () => clearInterval(timerId);
   }, [
     draffleClient.provider,
     draffleClient.provider.wallet,
     paymentOption.mint.address,
   ]);

  const maxTicketsToBuyable = useMemo(() => {
    if (!buyerTokenBalance.amount) return new u64(0);
    const newMax = buyerTokenBalance.amount
      .mul(paymentOption.dispenserPriceOut)
      .div(paymentOption.dispenserPriceIn)
      .div(raffle.proceeds.ticketPrice);

    if (
      paymentOption.mint.address.toString() ===
        buyerTokenBalance.mint.toString() &&
      newMax.ltn(ticketAmount)
    )
      setTicketAmount(newMax.toNumber());
    return newMax;
  }, [buyerTokenBalance, paymentOption]);

   useEffect(() => {
     if (!draffleClient.provider.wallet?.publicKey) return;
     let timerId: ReturnType<typeof setInterval>;

     const updateLamports = async () => {
       const newWalletLamports = await getWalletLamports(
         draffleClient.provider
       );
       setWalletLamports(newWalletLamports);
       if (
         isLamportsEnough(walletLamports) &&
         !(paymentOption.mint.address === wrappedSOL)
       ) {
         clearInterval(timerId);
       }
     };

     updateLamports();
     timerId = setInterval(() => {
       updateLamports();
     }, 5000);
     return () => clearInterval(timerId);
   }, [
     walletLamports,
     draffleClient.provider,
     draffleClient.provider.wallet.publicKey,
     paymentOption.mint.address,
   ]);
  if (!raffle) return null;
  return (
    <div className=" relative center mt-12 space-y-2 w-full flex flex-col ">
      <div style={{ maxWidth: '800px' }} className="backbutton w-full">
        <button
          onClick={() => push('/')}
          className="GTWalsheimProBold lg:p-2 p-1 rounded-md text-brand_secondary bg-brand_smooth_gray"
        >
          Back to Raffles
        </button>
      </div>
      <div
        style={{ maxWidth: '800px' }}
        className="bg-brand_smooth_gray rounded-xl w-full  lg:flex p-4"
      >
        <div className="col1 bg-green-00 w-full lg:w-2/5">
          <PrizeShowcaseOngoing prizes={raffle.prizes} />
        </div>
        <div className="col2 bg-red-00 w-full lg:w-3/5 pl-3">
          <div className="buttons flex justify-center lg:justify-start space-x-2 bg-red-00 py-4">
            <button
              onClick={() => setShowParticipants(false)}
              className={`${classes.cardButtonOne} primary_font ${
                showParticipants
                  ? 'bg-brand_primary text-white opacity-30'
                  : 'bg-brand_primary text-white'
              } `}
            >
              Details
            </button>
            <button
              onClick={() => setShowParticipants(true)}
              className={`${classes.cardButtonOne} primary_font ${
                !showParticipants
                  ? ' bg-brand_primary text-white opacity-30'
                  : 'bg-brand_primary text-white'
              } tracking-wider `}
            >
              Participants
            </button>
          </div>
          <div className="cardname bg-blue-00 pb-3">
            <p className={`${classes.cardTitle} primary_font`}>
              {
                //@ts-ignore
                raffle.prizes[0].meta.name
              }
            </p>
          </div>
          <div className="pricetext">
            {!showParticipants ? (
              <>
                <RaffleInfoSection
                  raffle={raffle}
                  userConnected={!!draffleClient.provider.wallet.publicKey}
                  userTickets={entrant?.tickets}
                />{' '}
                <div>
                  <Countdown
                    className={`${classes.cardCountdown} `}
                    endTimestamp={raffle.endTimestamp}
                    spacing={'1%'}
                  />
                </div>
                <div
                  className={`${classes.cardButtons} flex w-full justify-between`}
                >
                  <div className="-1+ flex items-center">
                    <div className="mincon minus">
                      <div className={classes.ticketAmountSectionLeft}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setTicketAmount((currentAmount) =>
                              Math.max(currentAmount - 1, 1)
                            )
                          }
                          disabled={ticketAmount <= 1}
                          className={
                            classes.changeTicketAmountButton +
                            ' bg-white text-white'
                          }
                        >
                          <IndeterminateCheckBoxRounded
                            className="text-white"
                            style={{ fontSize: 30 }}
                          />
                        </IconButton>
                      </div>
                    </div>
                    <div
                      className={
                        classes.ticketAmountSectionMiddle +
                        'amount text-white flex-1'
                      }
                    >
                      <p>{ticketAmount}</p>
                    </div>
                    <div className={classes.ticketAmountSectionRight + ' plus'}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setTicketAmount((currentAmount) => currentAmount + 1)
                        }
                        disabled={
                          raffle.totalTickets + ticketAmount >=
                            MAX_NUMBER_OF_PARTICIPANTS ||
                          !hasEnoughFundsToIncrementTicket ||
                          ticketAmount + 1 >
                            raffle.entrantsCap - raffle.totalTickets
                        }
                        className={classes.changeTicketAmountButton}
                      >
                        <AddBoxRounded
                          className="text-white"
                          style={{ fontSize: 30 }}
                        />
                      </IconButton>
                    </div>
                  </div>
                  <div
                    className={
                      'items-center flex capitalize justify-end  bg-red-00'
                    }
                  >
                    <button
                      className={
                        ticketAmount === 0 ||
                        raffle.totalTickets + ticketAmount >
                          MAX_NUMBER_OF_PARTICIPANTS ||
                        !hasEnoughFunds ||
                        purchaseOngoing
                          ? `bg-gray-900 text-gray-200 cursor-not-allowed primary_font ${classes.cardBuyButton}`
                          : `bg-brand_blue text-white hover:bg-brand_accent primary_font ${classes.cardBuyButton}`
                      }
                      onClick={onBuyTickets}
                      disabled={
                        ticketAmount === 0 ||
                        raffle.totalTickets + ticketAmount >
                          MAX_NUMBER_OF_PARTICIPANTS ||
                        !hasEnoughFunds ||
                        purchaseOngoing
                      }
                    >
                      <div>
                        {purchaseOngoing ? (
                          <>
                            <div className={classes.purchaseButtonContentLeft}>
                              <CircularProgress
                                size={20}
                                className={classes.purchaseSpinner}
                              />
                            </div>
                            <div
                              className={
                                classes.purchaseButtonContentMiddle +
                                ' primary_font'
                              }
                            >
                              Processing...
                            </div>
                            <div
                              className={classes.purchaseButtonContentRight}
                            />
                          </>
                        ) : (
                          <p className="text-xs capitalize primary_font">
                            Buy A Ticket{' '}
                            {!lamportsEnough && '(Insufficient BAL)'}
                          </p>
                        )}
                      </div>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <Participants raffle={raffle} />
            )}
          </div>
        </div>
      </div>
      <div style={{ maxWidth: '800px' }}>
        <Footer />
      </div>
    </div>
  );
};

interface IRaffleOngoingDetailsProps {
  raffle: Raffle;
  updateRaffle: () => void;
}

const RaffleOngoingScreenWithLayout: FC<IRaffleOngoingDetailsProps> = (
  props
) => {
  const { push } = useHistory();

  return (
    <Screen onBackNavigation={() => push(routes.HOME)}>
      <RaffleOngoingScreen {...props} />
    </Screen>
  );
};

export default RaffleOngoingScreenWithLayout;
